import { useState } from "react";

// ── DATA ──────────────────────────────────────────────────────────────────────

const PROFILE = {
  name: "Charhonda Manica",
  title: "Senior Product Support Specialist",
  location: "Louisville, KY",
  phone: "(502) 287-7462",
  email: "charhonda.manica@gmail.com",
  linkedin: "linkedin.com/in/charhondamanica",
  summary: "I'm a Senior Product Support Specialist with 7+ years of experience at the intersection of technical support, customer success, and product development. I've worked at startups and enterprise companies alike — from helping launch Credit Karma Savings to conducting user research for Hitachi's cellular therapy team. I bring a rare combination of deep technical skill and genuine care for the people I work with.",
  stats: [
    { value: "7+", label: "Years Experience" },
    { value: "20+", label: "Zapier Automations Built" },
    { value: "50+", label: "Daily Cases at Credit Karma" },
    { value: "5+", label: "SaaS Platforms Mastered" },
  ],
  skills: [
    { category: "Support & Success", items: ["Zendesk", "Salesforce", "Customer Success", "Account Management", "Technical Product Support", "QA Testing"] },
    { category: "Technical", items: ["SQL", "Zapier", "JIRA", "Airtable", "Twilio", "Python (learning)", "Prompt Engineering"] },
    { category: "Product", items: ["User Research", "Customer Development", "Process Documentation", "GTM Strategy", "Sprint Planning", "Persona Development"] },
  ],
  experience: [
    {
      role: "Senior Product Support Specialist",
      company: "Ceterus",
      location: "Charleston, SC",
      years: "March 2025 – Present",
      bullets: [
        "Provide technical support for accounting SaaS product used by small business owners nationwide",
        "Lead strategic process improvement for internal and client-facing recurring procedures",
        "Implement financial institution integrations to expand product capabilities",
      ]
    },
    {
      role: "Technical Operations Specialist II",
      company: "Ceterus",
      location: "Charleston, SC",
      years: "November 2021 – March 2025",
      bullets: [
        "Built and maintained 20+ Zapier automations connecting Salesforce, Asana, and 5+ third-party systems",
        "Leveraged SQL to troubleshoot and verify product issues across multiple internal data sources",
        "Served as QA tester for product development — proactively identifying, debugging, and reporting issues in JIRA",
        "Created data management and support processes adopted by offshore accounting teams",
      ]
    },
    {
      role: "Customer Success Lead",
      company: "Tulli.ai",
      location: "San Francisco, CA",
      years: "February 2020 – December 2020",
      bullets: [
        "Built the company's first customer support process from scratch — including escalation policy and standard procedures",
        "Conducted user research for product development: recruited participants, ran interviews, synthesized insights",
        "Created tutorial videos and onboarding guides that reduced new user friction significantly",
        "Tracked post-deployment bug reports to identify and resolve recurring product issues",
      ]
    },
    {
      role: "User Research Junior Specialist",
      company: "Mach49",
      location: "San Francisco, CA",
      years: "December 2020 – May 2021",
      bullets: [
        "Recruited and interviewed world-leading stem cell researchers for Hitachi's Japan product team",
        "Identified multiple viable product launch applications for Hitachi's cellular therapy initiative",
        "Synthesized weekly interview data into personas and customer development plans for Lixil water usage project",
      ]
    },
    {
      role: "Customer Support Specialist",
      company: "Credit Karma",
      location: "San Francisco, CA",
      years: "September 2019 – February 2020",
      bullets: [
        "Part of the launch team for Credit Karma Savings — a brand new financial product",
        "Managed 50+ cases per day using Zendesk and Salesforce Lightning",
        "Created help center content for both internal teams and end users",
      ]
    },
    {
      role: "Customer Support Specialist",
      company: "TrueAccord",
      location: "San Francisco, CA",
      years: "July 2018 – September 2019",
      bullets: [
        "Consistently ranked in the top 10% for quality and productivity across all call guidelines",
        "Used strong communication and negotiation skills to preserve revenue while maintaining customer satisfaction",
      ]
    },
  ],
  education: [
    {
      degree: "AS in AI Software Engineering",
      school: "Maestro University",
      years: "Oct 2025 – 2027",
      note: "Studying Python, Prompt Engineering, and Machine Learning"
    },
    {
      degree: "Product Management Certificate",
      school: "Product School · San Francisco, CA",
      years: "December 2020",
      note: "GTM strategies for SaaS products with emphasis on user and market research"
    },
  ],
};

const PM_PROJECTS = [
  {
    id: 1,
    category: "Outbound PM · User Research",
    company: "Mach49 × Hitachi",
    title: "Customer Development for Hitachi's Cell Therapy Product",
    color: "#c4622d",
    icon: "🔬",
    summary: "Recruited and interviewed the world's leading stem cell researchers on behalf of Hitachi's Japan-based product team — helping them understand the real barriers to bringing a new cellular therapy product to market.",
    achievements: [
      "Made introductions to the world's top cell development researchers for the Hitachi Japan team",
      "Identified multiple viable applications and launch avenues for the Hitachi product",
    ],
    responsibilities: [
      "Recruited participants for a cellular therapy research project targeting senior scientists globally",
      "Conducted in-depth interviews with top stem cell researchers to surface pain points and market barriers",
      "Synthesized weekly interview findings into data points and tested key product assumptions",
      "Developed customer stories and personas to guide Hitachi's product strategy",
    ],
    tags: ["User Research", "Customer Development", "Recruiting", "Healthcare", "Enterprise"],
  },
  {
    id: 2,
    category: "Outbound PM · User Research",
    company: "Mach49 × Lixil",
    title: "Customer Development for Lixil's Water Usage Research",
    color: "#5aa8e8",
    icon: "💧",
    summary: "Supported Lixil's residential and commercial water usage research project — gathering deep user insights to inform a new product development direction.",
    achievements: [],
    responsibilities: [
      "Recruited participants across residential and commercial segments for water usage research",
      "Synthesized weekly interviews into data points and tested key product assumptions",
      "Developed customer stories and personas to anchor the product development plan",
    ],
    tags: ["User Research", "Customer Development", "Recruiting", "Consumer Products"],
  },
  {
    id: 3,
    category: "PM Project · Customer Success",
    company: "Tulli.ai",
    title: "Onboarding Guide & Tutorial Experience",
    color: "#7e5ae8",
    icon: "🎬",
    summary: "Partnered with the design and product team to build Tulli's guided onboarding experience — reducing new user friction and showcasing the product's core value from day one.",
    achievements: [],
    responsibilities: [
      "Collaborated with the Design Principal and Lead Designer to build a guided onboarding experience for new users",
      "Created tutorial videos and written guides showcasing key features and real-world use cases",
      "Designed content to help users reach their 'aha moment' as quickly as possible",
    ],
    tags: ["Onboarding", "Content Design", "UX Writing", "Customer Success", "SaaS"],
  },
  {
    id: 4,
    category: "PM Project · Product Development",
    company: "Tulli.ai",
    title: "Feedback × Product Development Loop",
    color: "#4aa870",
    icon: "🔁",
    summary: "Built and ran a high-touch feedback loop between Tulli's users and the product team — turning raw customer insights into sprint priorities, sprint after sprint.",
    achievements: [],
    responsibilities: [
      "Implemented a structured follow-up process with new and existing users to gather qualitative app feedback",
      "Distilled interview insights to help the team identify which features to prioritize each development sprint",
      "Tracked all interview data in Airtable and JIRA to maintain a single source of truth for product decisions",
    ],
    tags: ["Product Feedback", "Sprint Planning", "Airtable", "JIRA", "User Interviews"],
  },
];

const BASE_RESUME = `CHARHONDA MANICA
Senior Product Support Specialist
Louisville, KY | (502) 287-7462 | charhonda.manica@gmail.com

SUMMARY
Experienced Senior Operations and Product Support Specialist with 7+ years in technical SaaS product support, user research, and customer success. Proficient in Zendesk, Salesforce, SQL, and Zapier. Adept at developing strategic processes and documentation. Specified training in Product Management. Currently pursuing AS in AI Software Engineering.

EXPERIENCE
Senior Product Support Specialist | Ceterus | March 2025 – Present
• Technical support for accounting SaaS product
• Strategic process improvement for internal and client-facing procedures
• Implementation of financial institution integrations

Technical Operations Specialist II | Ceterus | November 2021 – March 2025
• Built and maintained 20+ Zapier automations (Salesforce, Asana, 5+ systems)
• Leveraged SQL to troubleshoot and verify product issues
• QA tester for product development — identified, debugged, and reported issues in JIRA

Customer Success Lead | Tulli.ai | February 2020 – December 2020
• Built company's first customer support process and escalation policy from scratch
• Conducted user research for product development
• Created tutorial videos and onboarding guides for new users

User Research Junior Specialist | Mach49 | December 2020 – May 2021
• Recruited world-leading stem cell researchers for Hitachi Japan product team
• Synthesized weekly interview data into personas and product development plans for Lixil

Customer Support Specialist | Credit Karma | September 2019 – February 2020
• Launch team for Credit Karma Savings; 50+ cases/day via Zendesk and Salesforce

Customer Support Specialist | TrueAccord | July 2018 – September 2019
• Consistently top 10% in quality and productivity

SKILLS
Zendesk | Salesforce | SQL | Zapier | JIRA | Airtable | Twilio | Python (learning)
Customer Success | User Research | Process Documentation | QA Testing | GTM Strategy

EDUCATION
AS in AI Software Engineering | Maestro University | 2025–2027
Product Management Certificate | Product School, San Francisco | December 2020`;

// ── HELPERS ───────────────────────────────────────────────────────────────────

async function callClaude(system, user) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.REACT_APP_ANTHROPIC_API_KEY,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-calls": "true",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system,
      messages: [{ role: "user", content: user }],
    }),
  });
  const data = await res.json();
  return data.content?.[0]?.text || "";
}

// ── PORTFOLIO TAB ─────────────────────────────────────────────────────────────

function Portfolio() {
  const [openExp, setOpenExp] = useState(null);
  const [openProj, setOpenProj] = useState(null);

  return (
    <div className="tab-body">

      {/* HERO */}
      <div className="hero">
        {/* To add your photo: replace the src below with your image URL or import */}
        {/* e.g. import photo from './photo.jpg'; then use src={photo} */}
        <div className="hero-avatar">
          <img src={null} alt="Charhonda Manica"
            onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "none" }} />
          <span className="avatar-initials">CM</span>
        </div>
        <div className="hero-text">
          <h1 className="hero-name">{PROFILE.name}</h1>
          <div className="hero-title">{PROFILE.title}</div>
          <p className="hero-summary">{PROFILE.summary}</p>
          <div className="hero-contact">
            <a href={`mailto:${PROFILE.email}`}>{PROFILE.email}</a>
            <span>·</span>
            <span>{PROFILE.phone}</span>
            <span>·</span>
            <a href={`https://${PROFILE.linkedin}`} target="_blank" rel="noopener noreferrer">{PROFILE.linkedin}</a>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-row">
        {PROFILE.stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-val">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* PM PROJECTS */}
      <section>
        <div className="section-label">✦ Portfolio Projects</div>
        <h2 className="section-title">Product Management Work</h2>
        <p className="section-desc">Real PM projects — from user research with global enterprises to building product feedback systems at early-stage startups.</p>
        <div className="projects-grid">
          {PM_PROJECTS.map(p => (
            <div key={p.id} className="project-card"
              style={{ "--proj-color": p.color }}
              onClick={() => setOpenProj(openProj === p.id ? null : p.id)}>
              <div className="proj-top">
                <span className="proj-icon">{p.icon}</span>
                <span className="proj-category">{p.category}</span>
              </div>
              <div className="proj-company">{p.company}</div>
              <h3 className="proj-title">{p.title}</h3>
              <p className="proj-summary">{p.summary}</p>
              <div className="proj-tags">
                {p.tags.map(t => <span key={t} className="tag">{t}</span>)}
              </div>
              {openProj === p.id && (
                <div className="proj-detail">
                  {p.achievements.length > 0 && (
                    <div className="detail-block">
                      <div className="detail-label">🏆 Achievements</div>
                      {p.achievements.map((a, i) => <div key={i} className="detail-item achievement">— {a}</div>)}
                    </div>
                  )}
                  <div className="detail-block">
                    <div className="detail-label">📋 Responsibilities</div>
                    {p.responsibilities.map((r, i) => <div key={i} className="detail-item">— {r}</div>)}
                  </div>
                </div>
              )}
              <div className="proj-toggle">{openProj === p.id ? "Show less ↑" : "See full details ↓"}</div>
            </div>
          ))}
        </div>

        {/* TEARDOWN LINK */}
        <div className="teardown-banner" onClick={e => { e.stopPropagation(); window.open("/teardown.html", "_blank", "noopener,noreferrer"); }}>
          <div className="teardown-left">
            <span className="teardown-icon">🔍</span>
            <div>
              <div className="teardown-title">App Teardown Template</div>
              <div className="teardown-desc">My framework for analyzing any product through a PM lens — try it yourself</div>
            </div>
          </div>
          <span className="teardown-cta">Open Template ↗</span>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section>
        <div className="section-label">◈ Work History</div>
        <h2 className="section-title">Experience</h2>
        <div className="exp-list">
          {PROFILE.experience.map((e, i) => (
            <div key={i} className="exp-item" onClick={() => setOpenExp(openExp === i ? null : i)}>
              <div className="exp-header">
                <div>
                  <div className="exp-role">{e.role}</div>
                  <div className="exp-meta">{e.company} · {e.location} · {e.years}</div>
                </div>
                <span className="exp-toggle">{openExp === i ? "↑" : "↓"}</span>
              </div>
              {openExp === i && (
                <ul className="exp-bullets">
                  {e.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* SKILLS */}
      <section>
        <div className="section-label">⚙ Skills</div>
        <h2 className="section-title">What I Bring</h2>
        <div className="skills-grid">
          {PROFILE.skills.map(group => (
            <div key={group.category} className="skill-group">
              <div className="skill-category">{group.category}</div>
              <div className="skill-tags">
                {group.items.map(s => <span key={s} className="skill-tag">{s}</span>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EDUCATION */}
      <section>
        <div className="section-label">🎓 Education & Training</div>
        <h2 className="section-title">Certifications & Education</h2>
        <div className="edu-list">
          {PROFILE.education.map((ed, i) => (
            <div key={i} className="edu-card">
              <div className="edu-degree">{ed.degree}</div>
              <div className="edu-school">{ed.school} · {ed.years}</div>
              <div className="edu-note">{ed.note}</div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

// ── COVER LETTER TAB ──────────────────────────────────────────────────────────

function CoverLetter() {
  const [mode, setMode] = useState("live"); // "live" | "custom"
  const [liveJobs, setLiveJobs] = useState([]);
  const [liveJobsLoading, setLiveJobsLoading] = useState(false);
  const [liveJobsError, setLiveJobsError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDesc, setJobDesc] = useState("");
  const [company, setCompany] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [tone, setTone] = useState("Warm & Professional");
  const [length, setLength] = useState("3 paragraphs");
  const [focus, setFocus] = useState("Balanced");
  const [loading, setLoading] = useState(false);
  const [letter, setLetter] = useState("");
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState(1);

  const tones = ["Warm & Professional", "Confident & Direct", "Enthusiastic", "Concise & Sharp"];
  const lengths = ["2 paragraphs", "3 paragraphs", "4 paragraphs"];
  const focuses = ["Balanced", "Technical Skills", "Leadership & Process", "Customer Empathy", "PM & Product Work"];

  // Fetch live jobs from the same /api/jobs cron endpoint
  const fetchLiveJobs = async () => {
    setLiveJobsLoading(true);
    setLiveJobsError(null);
    setSelectedJobId(null);
    try {
      const res = await fetch("/api/jobs");
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setLiveJobs(data.jobs || []);
      setFetchedAt(data.fetchedAt);
    } catch (e) {
      setLiveJobsError("Could not load today's jobs. Make sure API keys are set in Vercel.");
    }
    setLiveJobsLoading(false);
  };

  const selectLiveJob = (job) => {
    setSelectedJobId(job.id);
    setRoleTitle(job.title);
    setCompany(job.company);
    setJobDesc(job.description || "");
  };

  const activeJobDesc = mode === "live"
    ? (liveJobs.find(j => j.id === selectedJobId)?.description || jobDesc)
    : jobDesc;

  const canGenerate = activeJobDesc.trim().length > 0;

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setLetter("");
    try {
      const focusInstruction = {
        "Balanced": "Balance technical skills, customer empathy, and process improvement equally.",
        "Technical Skills": "Emphasize SQL, Zapier automations, JIRA, API integrations, and technical troubleshooting.",
        "Leadership & Process": "Highlight process-building at Tulli.ai, Ceterus automation work, and cross-functional collaboration.",
        "Customer Empathy": "Lead with customer-first mindset — Credit Karma launch, TrueAccord top 10%, Tulli onboarding.",
        "PM & Product Work": "Emphasize the Mach49 user research projects, Tulli feedback loop, and Product School certificate.",
      }[focus];

      const lengthInstruction = {
        "2 paragraphs": "Write exactly 2 focused paragraphs.",
        "3 paragraphs": "Write exactly 3 paragraphs.",
        "4 paragraphs": "Write 4 paragraphs — intro, 2 experience paragraphs, and a closing.",
      }[length];

      const text = await callClaude(
        `You are an expert career coach writing a highly personalized cover letter. ${lengthInstruction} Tone: ${tone}. ${focusInstruction} Do NOT use generic filler phrases like "I am writing to express my interest." Start with something specific and compelling. Address it "Dear ${company ? company + " Hiring Team" : "Hiring Team"}," and sign off "Warm regards,\nCharhonda Manica".`,
        `Candidate: Charhonda Manica — Senior Product Support Specialist, 7+ years experience.

Full background:
- Senior Product Support Specialist at Ceterus (March 2025–Present): technical support for accounting SaaS, process improvement, financial institution integrations
- Technical Operations Specialist II at Ceterus (2021–2025): built 20+ Zapier automations (Salesforce, Asana, 5+ systems), SQL troubleshooting, JIRA QA testing
- Customer Success Lead at Tulli.ai (2020): built first support process from scratch, user research, created onboarding tutorials, tracked bugs
- User Research Specialist at Mach49 (2020–2021): recruited world-leading stem cell researchers for Hitachi Japan, ran customer development for Lixil water project, built personas and product plans
- Credit Karma (2019–2020): launch team for Credit Karma Savings, 50+ cases/day via Zendesk + Salesforce
- TrueAccord (2018–2019): top 10% in quality and productivity consistently
- Education: Product School PM Certificate (2020), AS in AI Software Engineering at Maestro University (in progress)
- Tools: Zendesk, Salesforce, SQL, Zapier, JIRA, Airtable, Twilio, Python (learning), Prompt Engineering

Role${roleTitle ? ": " + roleTitle : ""}${company ? " at " + company : ""}:
${activeJobDesc}

Write a cover letter that feels personal, specific, and genuine. Pick the 2-3 most relevant experiences for THIS role. Never sound generic.`
      );
      setLetter(text);
      setStep(2);
    } catch (e) {
      setLetter("Something went wrong. Please check your Anthropic API key in Vercel environment variables.");
      setStep(2);
    }
    setLoading(false);
  };

  const copy = () => {
    navigator.clipboard?.writeText(letter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPDF = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cover Letter — ${company || "Application"}</title>
          <style>
            body { font-family: Georgia, serif; max-width: 680px; margin: 60px auto; color: #2a1f14; font-size: 14px; line-height: 1.8; }
            .header { margin-bottom: 2rem; border-bottom: 1px solid #ddd; padding-bottom: 1rem; }
            .name { font-size: 20px; font-weight: bold; margin-bottom: 4px; }
            .contact { font-size: 12px; color: #666; }
            .date { margin-bottom: 1.5rem; color: #666; font-size: 13px; }
            pre { white-space: pre-wrap; font-family: Georgia, serif; font-size: 14px; line-height: 1.8; }
            @media print { body { margin: 40px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="name">Charhonda Manica</div>
            <div class="contact">charhonda.manica@gmail.com · (502) 287-7462 · Louisville, KY · linkedin.com/in/charhondamanica</div>
          </div>
          <div class="date">${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
          <pre>${letter}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const reset = () => {
    setStep(1);
    setLetter("");
    setCopied(false);
  };

  return (
    <div className="tab-body">
      <div className="tab-intro">
        <h2 className="tab-heading">Cover Letter Generator</h2>
        <p className="tab-desc">Pick from today's live job matches or paste your own — customize tone, length, and focus, then preview and download as PDF.</p>
      </div>

      {/* Step indicators */}
      <div className="cl-steps">
        <div className={`cl-step ${step >= 1 ? "active" : ""}`}>
          <span className="cl-step-num">1</span> Configure
        </div>
        <div className="cl-step-line" />
        <div className={`cl-step ${step >= 2 ? "active" : ""}`}>
          <span className="cl-step-num">2</span> Preview & Export
        </div>
      </div>

      {step === 1 && (
        <div className="cl-configure">

          {/* Mode toggle */}
          <div className="cl-mode-toggle">
            <button className={`cl-mode-btn ${mode === "live" ? "active" : ""}`} onClick={() => setMode("live")}>
              ◉ Today's live jobs
            </button>
            <button className={`cl-mode-btn ${mode === "custom" ? "active" : ""}`} onClick={() => setMode("custom")}>
              ✏️ Paste custom job
            </button>
          </div>

          {/* Live jobs picker */}
          {mode === "live" && (
            <div className="cl-saved-jobs">
              {/* Load / refresh bar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.8rem", flexWrap: "wrap", gap: "0.5rem" }}>
                {fetchedAt && (
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", color: "var(--muted)" }}>
                    Jobs from {new Date(fetchedAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                  </div>
                )}
                <button onClick={fetchLiveJobs} disabled={liveJobsLoading}
                  className="btn-ghost" style={{ padding: "0.3rem 0.8rem", fontSize: "0.75rem" }}>
                  {liveJobsLoading ? "Loading…" : liveJobs.length > 0 ? "↻ Refresh" : "Load Today's Jobs →"}
                </button>
              </div>

              {/* Error */}
              {liveJobsError && (
                <div className="error-block" style={{ marginBottom: "0.8rem" }}>
                  ⚠ {liveJobsError}
                </div>
              )}

              {/* Empty state */}
              {!liveJobsLoading && liveJobs.length === 0 && !liveJobsError && (
                <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--muted)", fontSize: "0.85rem", background: "var(--warm)", borderRadius: "10px", border: "1px dashed rgba(196,98,45,0.2)" }}>
                  Click "Load Today's Jobs" to pull the latest matches from your job feed
                </div>
              )}

              {/* Loading skeleton */}
              {liveJobsLoading && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {[1,2,3].map(i => (
                    <div key={i} style={{ height: "62px", background: "var(--warm)", borderRadius: "10px", border: "1px solid var(--border)", animation: "pulse 1.5s ease infinite" }} />
                  ))}
                </div>
              )}

              {/* Job list */}
              {liveJobs.slice(0, 10).map(job => {
                const matchColor = job.match >= 90 ? "#4aa870" : job.match >= 80 ? "#c4a020" : "#a08070";
                return (
                  <div key={job.id}
                    className={`cl-job-option ${selectedJobId === job.id ? "selected" : ""}`}
                    onClick={() => selectLiveJob(job)}>
                    <div className="cl-job-check">{selectedJobId === job.id ? "✓" : ""}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="cl-job-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        {job.title} — <span style={{ color: "var(--accent)" }}>{job.company}</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", color: matchColor, background: `color-mix(in srgb, ${matchColor} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${matchColor} 30%, transparent)`, padding: "0.1rem 0.4rem", borderRadius: "4px", marginLeft: "auto" }}>
                          {job.match}% match
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "0.35rem", marginTop: "0.3rem", flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", color: "var(--muted)" }}>{job.location}</span>
                        {job.salary && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", color: "var(--muted)" }}>· {job.salary}</span>}
                        {job.tags?.slice(0, 3).map(t => <span key={t} className="tag" style={{ fontSize: "0.62rem" }}>{t}</span>)}
                        {job.url && job.url !== "#" && (
                          <a href={job.url} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", color: "var(--accent)", marginLeft: "auto", textDecoration: "none" }}>
                            View ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Custom job input */}
          {mode === "custom" && (
            <div className="cl-custom">
              <div className="form-row" style={{ marginBottom: "0.75rem" }}>
                <div className="form-field">
                  <label className="form-label">Role Title</label>
                  <input value={roleTitle} onChange={e => setRoleTitle(e.target.value)}
                    placeholder="e.g. Technical Support Engineer" className="form-input" />
                </div>
                <div className="form-field">
                  <label className="form-label">Company</label>
                  <input value={company} onChange={e => setCompany(e.target.value)}
                    placeholder="e.g. HubSpot" className="form-input" />
                </div>
              </div>
              <div className="form-field" style={{ marginBottom: "1rem" }}>
                <label className="form-label">Job Description</label>
                <textarea value={jobDesc} onChange={e => setJobDesc(e.target.value)}
                  placeholder="Paste the full job description here…" className="form-textarea" />
              </div>
            </div>
          )}

          {/* Style controls */}
          <div className="cl-controls">
            <div className="cl-control-group">
              <div className="form-label" style={{ marginBottom: "0.5rem" }}>Tone</div>
              <div className="cl-pill-row">
                {tones.map(t => (
                  <button key={t} onClick={() => setTone(t)} className={`cl-pill ${tone === t ? "active" : ""}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="cl-control-group">
              <div className="form-label" style={{ marginBottom: "0.5rem" }}>Length</div>
              <div className="cl-pill-row">
                {lengths.map(l => (
                  <button key={l} onClick={() => setLength(l)} className={`cl-pill ${length === l ? "active" : ""}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="cl-control-group">
              <div className="form-label" style={{ marginBottom: "0.5rem" }}>Highlight Focus</div>
              <div className="cl-pill-row">
                {focuses.map(f => (
                  <button key={f} onClick={() => setFocus(f)} className={`cl-pill ${focus === f ? "active" : ""}`}>{f}</button>
                ))}
              </div>
            </div>
          </div>

          <button onClick={generate} disabled={loading || !canGenerate}
            className={`btn-primary ${(!canGenerate || loading) ? "btn-disabled" : ""}`}
            style={{ marginTop: "1.5rem" }}>
            {loading ? "✦ Writing your letter…" : "Generate Cover Letter →"}
          </button>

          {loading && (
            <div className="cl-loading">
              <div className="cl-loading-bar"><div className="cl-loading-fill" /></div>
              <div className="loading-text">Claude is writing a personalized letter tailored to this role…</div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="cl-preview">
          {/* Meta bar */}
          <div className="cl-preview-meta">
            <div>
              <span className="cl-meta-label">Role</span>
              <span className="cl-meta-val">{roleTitle || "Custom Role"}</span>
            </div>
            {company && (
              <div>
                <span className="cl-meta-label">Company</span>
                <span className="cl-meta-val">{company}</span>
              </div>
            )}
            <div>
              <span className="cl-meta-label">Tone</span>
              <span className="cl-meta-val">{tone}</span>
            </div>
            <div>
              <span className="cl-meta-label">Focus</span>
              <span className="cl-meta-val">{focus}</span>
            </div>
          </div>

          {/* Letter preview */}
          <div className="cl-letter-preview">
            <div className="cl-letter-header">
              <div className="cl-letter-name">Charhonda Manica</div>
              <div className="cl-letter-contact">charhonda.manica@gmail.com · (502) 287-7462 · Louisville, KY</div>
              <div className="cl-letter-date">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
            </div>
            <div className="cl-letter-body">{letter}</div>
          </div>

          {/* Action buttons */}
          <div className="cl-actions">
            <button onClick={reset} className="btn-ghost">← Edit & Regenerate</button>
            <div style={{ display: "flex", gap: "0.6rem" }}>
              <button onClick={copy} className="btn-ghost">
                {copied ? "✓ Copied!" : "Copy Text"}
              </button>
              <button onClick={downloadPDF} className="btn-primary" style={{ margin: 0 }}>
                Download PDF ↓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── LIVE JOBS TAB ─────────────────────────────────────────────────────────────

function LiveJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [sources, setSources] = useState(null);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filter, setFilter] = useState("All");

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/jobs");
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setJobs(data.jobs || []);
      setFetchedAt(data.fetchedAt);
      setSources(data.sources);
      setHasLoaded(true);
    } catch (e) {
      setError("Could not load jobs. Make sure API keys are set in Vercel Environment Variables.");
    }
    setLoading(false);
  };

  const filterOptions = ["All", "Remote", "90%+ Match", "Remotive", "Greenhouse", "Adzuna", "Google Jobs"];
  const sourceColors = { Remotive: "#c4622d", Greenhouse: "#5aa8e8", Adzuna: "#7e5ae8", "Google Jobs": "#4aa870" };

  const filtered = jobs.filter(j => {
    if (filter === "Remote") return j.location?.toLowerCase().includes("remote");
    if (filter === "90%+ Match") return j.match >= 90;
    if (["Remotive", "Greenhouse", "Adzuna", "Google Jobs"].includes(filter)) return j.source === filter;
    return true;
  });

  return (
    <div className="tab-body">
      <div className="tab-intro">
        <h2 className="tab-heading">Today's Job Matches</h2>
        <p className="tab-desc">Fresh listings pulled from Remotive, Greenhouse, Adzuna, and Google Jobs — scored against your background by Claude AI. Auto-refreshes daily at 8am.</p>
      </div>

      {sources && (
        <div className="source-row">
          {Object.entries(sources).map(([src, count]) => {
            const label = src === "serpapi" ? "Google Jobs" : src.charAt(0).toUpperCase() + src.slice(1);
            const color = sourceColors[label] || "#a08070";
            return <div key={src} className="source-badge" style={{ "--src-color": color }}>{label}: {count}</div>;
          })}
          {fetchedAt && <div className="fetch-time">Updated {new Date(fetchedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
        </div>
      )}

      {hasLoaded && (
        <div className="filter-row">
          {filterOptions.map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`filter-btn ${filter === f ? "active" : ""}`}>{f}</button>
          ))}
        </div>
      )}

      {!hasLoaded && !loading && <button onClick={fetchJobs} className="btn-primary">Fetch Today's Jobs →</button>}
      {hasLoaded && !loading && <button onClick={fetchJobs} className="btn-ghost" style={{ marginBottom: "1.2rem" }}>↻ Refresh</button>}

      {loading && (
        <div className="loading-block">
          <div className="loading-text">Fetching and scoring jobs with Claude AI…</div>
          <div className="loading-sub">This takes 10–20 seconds on first load</div>
        </div>
      )}

      {error && (
        <div className="error-block">
          <div>⚠ {error}</div>
          <div className="error-sub">Add to Vercel: ANTHROPIC_API_KEY, ADZUNA_APP_ID, ADZUNA_API_KEY, SERP_API_KEY</div>
        </div>
      )}

      <div className="jobs-list">
        {filtered.map(job => {
          const srcColor = sourceColors[job.source] || "#a08070";
          const matchColor = job.match >= 90 ? "#4aa870" : job.match >= 80 ? "#c4a020" : "#a08070";
          return (
            <div key={job.id} className="job-card">
              <div className="job-title-row">
                <span className="job-title">{job.title}</span>
                <span className="match-badge" style={{ "--match-color": matchColor }}>{job.match}% match</span>
                <span className="source-tag" style={{ "--src-color": srcColor }}>{job.source}</span>
              </div>
              <div className="job-meta">{job.company} · {job.location}{job.salary ? ` · ${job.salary}` : ""}</div>
              {job.matchReason && <div className="match-reason">✦ {job.matchReason}</div>}
              {job.description && <div className="job-desc">{job.description.slice(0, 180)}…</div>}
              <div className="job-footer">
                {job.tags?.slice(0, 4).map(t => <span key={t} className="tag">{t}</span>)}
                <a href={job.url} target="_blank" rel="noopener noreferrer" className="view-link">View Job ↗</a>
              </div>
            </div>
          );
        })}
        {hasLoaded && filtered.length === 0 && !loading && (
          <div className="empty-state">No jobs match this filter — try a different one.</div>
        )}
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: "portfolio", label: "Portfolio", icon: "◈" },
  { id: "jobs", label: "Live Jobs", icon: "◉" },
  { id: "cover", label: "Cover Letter", icon: "✉" },
];

export default function App() {
  const [tab, setTab] = useState("portfolio");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --bg: #faf7f2; --surface: #ffffff; --border: rgba(0,0,0,0.08);
          --text: #2a1f14; --muted: #8a7a6a; --accent: #c4622d; --accent-light: rgba(196,98,45,0.1); --warm: #f5ede0;
        }
        body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }

        .nav { position:sticky; top:0; z-index:100; background:rgba(250,247,242,0.96); backdrop-filter:blur(12px); border-bottom:1px solid var(--border); padding:0 2rem; display:flex; align-items:center; justify-content:space-between; height:64px; }
        .nav-brand { font-family:'Lora',serif; font-size:1.1rem; color:var(--text); }
        .nav-sub { font-family:'DM Mono',monospace; font-size:0.6rem; color:var(--muted); letter-spacing:0.12em; margin-top:0.1rem; }
        .nav-tabs { display:flex; gap:0.25rem; background:rgba(0,0,0,0.05); border-radius:10px; padding:4px; }
        .nav-tab { background:none; border:none; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; padding:0.45rem 1rem; border-radius:7px; color:var(--muted); transition:all 0.15s; display:flex; align-items:center; gap:0.4rem; white-space:nowrap; }
        .nav-tab.active { background:var(--accent); color:white; font-weight:600; }
        .nav-tab:hover:not(.active) { background:rgba(0,0,0,0.06); color:var(--text); }

        .main { max-width:860px; margin:0 auto; padding:2.5rem 1.5rem 6rem; }
        .tab-body { animation:fadeUp 0.3s ease; }

        .hero { display:flex; gap:1.8rem; align-items:flex-start; background:var(--surface); border:1px solid var(--border); border-radius:16px; padding:2rem; margin-bottom:1.5rem; }
        .hero-avatar { width:72px; height:72px; border-radius:50%; background:linear-gradient(135deg,#c4622d,#e8845a); color:white; font-family:'Lora',serif; font-size:1.4rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; position:relative; }
        .avatar-initials { display:flex; align-items:center; justify-content:center; width:100%; height:100%; }
        .hero-name { font-family:'Lora',serif; font-size:1.7rem; font-weight:700; margin-bottom:0.2rem; }
        .hero-title { font-family:'DM Mono',monospace; font-size:0.7rem; letter-spacing:0.12em; color:var(--accent); text-transform:uppercase; margin-bottom:0.8rem; }
        .hero-summary { font-size:0.92rem; line-height:1.75; color:#5a4a3a; margin-bottom:0.9rem; }
        .hero-contact { display:flex; gap:0.6rem; align-items:center; flex-wrap:wrap; font-size:0.8rem; color:var(--muted); }
        .hero-contact a { color:var(--accent); text-decoration:none; }
        .hero-contact a:hover { text-decoration:underline; }

        .stats-row { display:grid; grid-template-columns:repeat(4,1fr); gap:0.75rem; margin-bottom:2.5rem; }
        .stat-card { background:var(--warm); border:1px solid rgba(196,98,45,0.15); border-radius:12px; padding:1.1rem; text-align:center; }
        .stat-val { font-family:'Lora',serif; font-size:1.9rem; color:var(--accent); line-height:1; margin-bottom:0.3rem; }
        .stat-label { font-family:'DM Mono',monospace; font-size:0.6rem; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; }

        section { margin-bottom:3rem; }
        .section-label { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--accent); letter-spacing:0.18em; text-transform:uppercase; margin-bottom:0.4rem; }
        .section-title { font-family:'Lora',serif; font-size:1.4rem; font-weight:700; margin-bottom:0.5rem; }
        .section-desc { font-size:0.88rem; color:var(--muted); line-height:1.7; margin-bottom:1.4rem; }

        .projects-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(340px,1fr)); gap:1rem; }
        .project-card { background:var(--surface); border:1.5px solid var(--border); border-radius:14px; padding:1.5rem; cursor:pointer; transition:all 0.2s; position:relative; overflow:hidden; }
        .project-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--proj-color); }
        .project-card:hover { border-color:var(--proj-color); box-shadow:0 4px 20px rgba(0,0,0,0.07); transform:translateY(-2px); }
        .proj-top { display:flex; align-items:center; gap:0.6rem; margin-bottom:0.4rem; }
        .proj-icon { font-size:1.2rem; }
        .proj-category { font-family:'DM Mono',monospace; font-size:0.62rem; color:var(--muted); }
        .proj-company { font-family:'DM Mono',monospace; font-size:0.72rem; color:var(--proj-color); font-weight:500; margin-bottom:0.3rem; }
        .proj-title { font-family:'Lora',serif; font-size:1rem; font-weight:700; margin-bottom:0.6rem; line-height:1.35; }
        .proj-summary { font-size:0.84rem; color:#7a6a5a; line-height:1.65; margin-bottom:0.8rem; }
        .proj-tags { display:flex; flex-wrap:wrap; gap:0.35rem; margin-bottom:0.8rem; }
        .proj-detail { margin-top:1rem; padding-top:1rem; border-top:1px solid var(--border); animation:fadeUp 0.2s ease; }
        .detail-block { margin-bottom:1rem; }
        .detail-label { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--muted); letter-spacing:0.1em; text-transform:uppercase; margin-bottom:0.5rem; }
        .detail-item { font-size:0.84rem; color:#5a4a3a; line-height:1.7; padding:0.2rem 0; }
        .achievement { color:#3a6a4a; font-weight:500; }
        .proj-toggle { font-family:'DM Mono',monospace; font-size:0.68rem; color:var(--accent); margin-top:0.4rem; }

        /* TEARDOWN BANNER */
        .teardown-banner {
          display:flex; align-items:center; justify-content:space-between;
          margin-top:1.2rem; padding:1.1rem 1.4rem;
          background:var(--warm); border:1.5px solid rgba(196,98,45,0.2);
          border-radius:12px; cursor:pointer;
          transition:all 0.2s; gap:1rem;
        }
        .teardown-banner:hover { border-color:var(--accent); background:#f0e4d0; transform:translateY(-1px); box-shadow:0 4px 16px rgba(196,98,45,0.12); }
        .teardown-left { display:flex; align-items:center; gap:1rem; }
        .teardown-icon { font-size:1.4rem; }
        .teardown-title { font-family:'Lora',serif; font-size:0.95rem; font-weight:700; color:var(--text); margin-bottom:0.2rem; }
        .teardown-desc { font-size:0.8rem; color:var(--muted); line-height:1.5; }
        .teardown-cta { font-family:'DM Mono',monospace; font-size:0.72rem; font-weight:600; color:var(--accent); white-space:nowrap; background:rgba(196,98,45,0.1); padding:0.4rem 0.9rem; border-radius:6px; border:1px solid rgba(196,98,45,0.25); }

        .exp-list { display:flex; flex-direction:column; gap:0.5rem; }
        .exp-item { background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:1.1rem 1.3rem; cursor:pointer; transition:all 0.15s; }
        .exp-item:hover { border-color:rgba(196,98,45,0.3); }
        .exp-header { display:flex; align-items:center; justify-content:space-between; }
        .exp-role { font-weight:600; font-size:0.95rem; margin-bottom:0.2rem; }
        .exp-meta { font-family:'DM Mono',monospace; font-size:0.68rem; color:var(--muted); }
        .exp-toggle { color:var(--accent); font-size:1rem; flex-shrink:0; margin-left:1rem; }
        .exp-bullets { padding:0.8rem 0 0.2rem 1rem; display:flex; flex-direction:column; gap:0.4rem; animation:fadeUp 0.2s ease; }
        .exp-bullets li { font-size:0.86rem; color:#5a4a3a; line-height:1.65; }

        .skills-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:1rem; }
        .skill-group { background:var(--warm); border:1px solid rgba(196,98,45,0.12); border-radius:10px; padding:1.1rem; }
        .skill-category { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--accent); letter-spacing:0.1em; text-transform:uppercase; margin-bottom:0.7rem; }
        .skill-tags { display:flex; flex-wrap:wrap; gap:0.35rem; }
        .skill-tag { font-size:0.78rem; color:#5a4a3a; background:white; border:1px solid rgba(0,0,0,0.1); padding:0.25rem 0.6rem; border-radius:5px; }

        .edu-list { display:flex; flex-direction:column; gap:0.75rem; }
        .edu-card { background:var(--surface); border:1px solid var(--border); border-left:3px solid var(--accent); border-radius:10px; padding:1.2rem 1.4rem; }
        .edu-degree { font-weight:600; font-size:0.95rem; margin-bottom:0.2rem; }
        .edu-school { font-family:'DM Mono',monospace; font-size:0.68rem; color:var(--muted); margin-bottom:0.4rem; }
        .edu-note { font-size:0.84rem; color:#7a6a5a; line-height:1.6; }

        .tab-intro { margin-bottom:1.8rem; }
        .tab-heading { font-family:'Lora',serif; font-size:1.6rem; font-weight:700; margin-bottom:0.4rem; }
        .tab-desc { font-size:0.9rem; color:var(--muted); line-height:1.7; }
        .form-row { display:grid; grid-template-columns:1fr auto; gap:0.75rem; margin-bottom:0.75rem; align-items:end; }
        .form-field { display:flex; flex-direction:column; gap:0.35rem; }
        .form-label { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--muted); letter-spacing:0.1em; text-transform:uppercase; }
        .form-input { background:var(--surface); border:1.5px solid var(--border); border-radius:8px; padding:0.65rem 0.9rem; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:var(--text); outline:none; transition:border-color 0.15s; }
        .form-input:focus { border-color:var(--accent); }
        .form-textarea { width:100%; min-height:160px; resize:vertical; background:var(--surface); border:1.5px solid var(--border); border-radius:10px; padding:1rem; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:var(--text); line-height:1.7; outline:none; transition:border-color 0.15s; }
        .form-textarea:focus { border-color:var(--accent); }
        .letter-output { margin-top:1.5rem; animation:fadeUp 0.3s ease; }
        .letter-body { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:1.8rem; font-size:0.92rem; line-height:1.85; color:#3a2a1a; white-space:pre-wrap; margin-bottom:0.8rem; }

        .source-row { display:flex; gap:0.5rem; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
        .source-badge { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--src-color); background:color-mix(in srgb,var(--src-color) 10%,transparent); border:1px solid color-mix(in srgb,var(--src-color) 25%,transparent); padding:0.25rem 0.65rem; border-radius:5px; }
        .fetch-time { font-family:'DM Mono',monospace; font-size:0.65rem; color:var(--muted); margin-left:auto; }
        .filter-row { display:flex; gap:0.35rem; flex-wrap:wrap; margin-bottom:1.2rem; }
        .filter-btn { font-family:'DM Mono',monospace; font-size:0.65rem; padding:0.35rem 0.8rem; border-radius:6px; border:1px solid var(--border); background:transparent; color:var(--muted); cursor:pointer; transition:all 0.15s; }
        .filter-btn.active { background:var(--accent-light); border-color:var(--accent); color:var(--accent); }
        .jobs-list { display:flex; flex-direction:column; gap:0.75rem; }
        .job-card { background:var(--surface); border:1.5px solid var(--border); border-radius:12px; padding:1.1rem 1.3rem; transition:border-color 0.15s; }
        .job-card:hover { border-color:rgba(196,98,45,0.3); }
        .job-title-row { display:flex; align-items:center; gap:0.6rem; flex-wrap:wrap; margin-bottom:0.25rem; }
        .job-title { font-weight:600; font-size:0.95rem; }
        .match-badge { font-family:'DM Mono',monospace; font-size:0.65rem; font-weight:600; color:var(--match-color); background:color-mix(in srgb,var(--match-color) 12%,transparent); border:1px solid color-mix(in srgb,var(--match-color) 30%,transparent); padding:0.15rem 0.5rem; border-radius:4px; }
        .source-tag { font-family:'DM Mono',monospace; font-size:0.6rem; color:var(--src-color); background:color-mix(in srgb,var(--src-color) 10%,transparent); border:1px solid color-mix(in srgb,var(--src-color) 25%,transparent); padding:0.12rem 0.45rem; border-radius:4px; }
        .job-meta { font-family:'DM Mono',monospace; font-size:0.68rem; color:var(--muted); margin-bottom:0.35rem; }
        .match-reason { font-size:0.78rem; color:#4a7a5a; font-style:italic; margin-bottom:0.3rem; }
        .job-desc { font-size:0.82rem; color:#8a7a6a; line-height:1.6; margin-bottom:0.6rem; }
        .job-footer { display:flex; align-items:center; flex-wrap:wrap; gap:0.4rem; }
        .view-link { font-family:'DM Mono',monospace; font-size:0.68rem; color:var(--accent); background:var(--accent-light); border:1px solid rgba(196,98,45,0.25); padding:0.2rem 0.65rem; border-radius:5px; text-decoration:none; margin-left:auto; }
        .view-link:hover { background:rgba(196,98,45,0.18); }
        .loading-block { padding:2rem 0; }
        .loading-text { font-family:'DM Mono',monospace; font-size:0.75rem; color:var(--muted); margin-bottom:0.4rem; }
        .loading-sub { font-size:0.78rem; color:#bbb; }
        .error-block { background:rgba(200,80,60,0.05); border:1px solid rgba(200,80,60,0.2); border-radius:10px; padding:1rem 1.2rem; color:#c85040; font-size:0.85rem; margin-bottom:1rem; }
        .error-sub { margin-top:0.4rem; font-size:0.75rem; color:#a07060; }
        .empty-state { font-family:'DM Mono',monospace; font-size:0.78rem; color:var(--muted); padding:2rem 0; }

        .tag { font-size:0.72rem; color:var(--muted); background:var(--warm); border:1px solid rgba(0,0,0,0.08); padding:0.2rem 0.55rem; border-radius:4px; }
        .btn-primary { padding:0.75rem 2rem; border-radius:10px; border:none; background:var(--accent); color:white; font-family:'DM Sans',sans-serif; font-weight:600; font-size:0.9rem; cursor:pointer; transition:all 0.2s; margin-bottom:1.2rem; }
        .btn-primary:hover { background:#a8501f; }
        .btn-disabled { background:#d0c0b0 !important; cursor:not-allowed !important; color:#a09080 !important; }
        .btn-ghost { padding:0.45rem 1.1rem; border-radius:7px; border:1px solid var(--border); background:transparent; color:var(--muted); font-size:0.8rem; cursor:pointer; transition:all 0.15s; }
        .btn-ghost:hover { border-color:var(--accent); color:var(--accent); }

        /* COVER LETTER ENHANCED */
        .cl-steps { display:flex; align-items:center; gap:0; margin-bottom:2rem; }
        .cl-step { display:flex; align-items:center; gap:0.5rem; font-family:'DM Mono',monospace; font-size:0.7rem; color:var(--muted); letter-spacing:0.08em; }
        .cl-step.active { color:var(--accent); }
        .cl-step-num { width:22px; height:22px; border-radius:50%; border:1.5px solid currentColor; display:flex; align-items:center; justify-content:center; font-size:0.65rem; font-weight:700; }
        .cl-step-line { flex:1; height:1px; background:var(--border); margin:0 0.8rem; max-width:60px; }

        .cl-mode-toggle { display:flex; gap:0.5rem; margin-bottom:1.2rem; }
        .cl-mode-btn { flex:1; padding:0.7rem 1rem; border-radius:9px; border:1.5px solid var(--border); background:transparent; font-family:'DM Sans',sans-serif; font-size:0.85rem; color:var(--muted); cursor:pointer; transition:all 0.15s; text-align:center; }
        .cl-mode-btn.active { border-color:var(--accent); background:var(--accent-light); color:var(--accent); font-weight:600; }

        .cl-saved-jobs { display:flex; flex-direction:column; gap:0.5rem; margin-bottom:1.4rem; }
        .cl-job-option { display:flex; align-items:center; gap:1rem; padding:0.9rem 1.1rem; border-radius:10px; border:1.5px solid var(--border); cursor:pointer; transition:all 0.15s; background:var(--surface); }
        .cl-job-option:hover { border-color:rgba(196,98,45,0.3); }
        .cl-job-option.selected { border-color:var(--accent); background:var(--accent-light); }
        .cl-job-check { width:20px; height:20px; border-radius:5px; border:1.5px solid var(--accent); background:var(--accent); color:white; display:flex; align-items:center; justify-content:center; font-size:0.7rem; font-weight:700; flex-shrink:0; opacity:0; transition:opacity 0.15s; }
        .cl-job-option.selected .cl-job-check { opacity:1; }
        .cl-job-title { font-size:0.9rem; font-weight:500; color:var(--text); margin-bottom:0.3rem; }
        .cl-job-tags { display:flex; gap:0.35rem; flex-wrap:wrap; }

        .cl-controls { display:flex; flex-direction:column; gap:1.1rem; padding:1.3rem; background:var(--warm); border-radius:12px; border:1px solid rgba(196,98,45,0.12); margin-top:1rem; }
        .cl-control-group {}
        .cl-pill-row { display:flex; flex-wrap:wrap; gap:0.4rem; }
        .cl-pill { padding:0.35rem 0.85rem; border-radius:20px; border:1px solid var(--border); background:white; font-family:'DM Sans',sans-serif; font-size:0.78rem; color:var(--muted); cursor:pointer; transition:all 0.15s; }
        .cl-pill.active { background:var(--accent); border-color:var(--accent); color:white; font-weight:500; }
        .cl-pill:hover:not(.active) { border-color:rgba(196,98,45,0.3); color:var(--text); }

        .cl-loading { margin-top:1rem; }
        .cl-loading-bar { height:3px; background:var(--border); border-radius:2px; overflow:hidden; margin-bottom:0.6rem; }
        .cl-loading-fill { height:100%; background:var(--accent); border-radius:2px; animation:loadingBar 2s ease-in-out infinite; }
        @keyframes loadingBar { 0%{width:0%} 50%{width:70%} 100%{width:95%} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .cl-preview { animation:fadeUp 0.3s ease; }
        .cl-preview-meta { display:flex; gap:1.5rem; flex-wrap:wrap; padding:0.9rem 1.2rem; background:var(--warm); border-radius:10px; border:1px solid rgba(196,98,45,0.12); margin-bottom:1.2rem; }
        .cl-meta-label { font-family:'DM Mono',monospace; font-size:0.6rem; color:var(--muted); text-transform:uppercase; letter-spacing:0.1em; display:block; margin-bottom:0.15rem; }
        .cl-meta-val { font-size:0.82rem; font-weight:500; color:var(--text); }

        .cl-letter-preview { background:var(--surface); border:1px solid var(--border); border-radius:14px; padding:2.2rem 2.4rem; margin-bottom:1.2rem; box-shadow:0 2px 12px rgba(0,0,0,0.04); }
        .cl-letter-header { border-bottom:1px solid var(--border); padding-bottom:1.1rem; margin-bottom:1.4rem; }
        .cl-letter-name { font-family:'Lora',serif; font-size:1.2rem; font-weight:700; color:var(--text); margin-bottom:0.25rem; }
        .cl-letter-contact { font-size:0.78rem; color:var(--muted); margin-bottom:0.5rem; }
        .cl-letter-date { font-size:0.78rem; color:var(--muted); }
        .cl-letter-body { font-size:0.92rem; line-height:1.9; color:#3a2a1a; white-space:pre-wrap; }

        .cl-actions { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:0.8rem; }

        select option { background: white; }

        @media (max-width:600px) {
          .nav { padding:0 1rem; }
          .nav-brand, .nav-sub { display:none; }
          .stats-row { grid-template-columns:repeat(2,1fr); }
          .hero { flex-direction:column; gap:1rem; }
          .projects-grid { grid-template-columns:1fr; }
          .form-row { grid-template-columns:1fr; }
          .main { padding:1.5rem 1rem 5rem; }
          .cl-preview-meta { gap:0.8rem; }
          .cl-letter-preview { padding:1.4rem; }
          .cl-actions { flex-direction:column; align-items:stretch; }
          .cl-actions > div { display:flex; gap:0.6rem; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
        <nav className="nav">
          <div>
            <div className="nav-brand">Charhonda Manica</div>
            <div className="nav-sub">Senior Product Support Specialist</div>
          </div>
          <div className="nav-tabs">
            {TABS.map(t => (
              <button key={t.id} className={`nav-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                <span>{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
        </nav>
        <main className="main">
          {tab === "portfolio" && <Portfolio />}
          {tab === "jobs" && <LiveJobs />}
          {tab === "cover" && <CoverLetter />}
        </main>
      </div>
    </>
  );
}
